import React from 'react';
import { useLocation } from 'react-router-dom';
import { useContent } from '../hooks/useContent';
import { LandingPageTemplate } from '../pages/LandingPageTemplate';
import NotFoundPage from '../pages/NotFoundPage';
import { SpinnerIcon } from '../components/icons/SpinnerIcon';

export const DynamicRouteHandler: React.FC = () => {
    const location = useLocation();
    const { content, isLoaded, redirects } = useContent();
    
    // Synchronously check for redirects as soon as data is loaded.
    if (isLoaded && redirects) {
        const currentPath = location.pathname;
        const matchingRedirect = redirects.find(r => r.source === currentPath);
        if (matchingRedirect) {
            // Initiate redirect immediately.
            window.location.replace(matchingRedirect.destination);
            // Return a loading state to prevent a flash of incorrect content while redirecting.
            return (
                <div className="flex items-center justify-center h-screen bg-white">
                    <SpinnerIcon />
                    <span className="ml-2">Redirecting...</span>
                </div>
            );
        }
    }
    
    // Show a loading spinner until content and redirects are confirmed.
    if (!isLoaded) {
        return (
            <div className="flex items-center justify-center h-screen bg-white">
                <SpinnerIcon />
            </div>
        );
    }

    const currentPath = location.pathname;

    // Helper to normalize paths by removing trailing slashes (except for root "/")
    // This ensures that "/my-page" and "/my-page/" are treated as the same page.
    const normalizePath = (path: string) => {
        return path === '/' ? path : path.replace(/\/$/, '');
    };

    const dynamicPageRouteKey = Object.keys(content).find(key => {
        const pageSlug = content[key]?.seo?.slug;
        if (!pageSlug) return false;
        return normalizePath(pageSlug) === normalizePath(currentPath);
    });
    
    if (dynamicPageRouteKey) {
        return <LandingPageTemplate contentKey={dynamicPageRouteKey} />;
    }
    
    // If no static or dynamic route matched, show 404.
    return <NotFoundPage />;
};